// src/Views/StoreDetail.js
import React, { useContext } from 'react';
import { StoreContext } from '../contexts/StoreContext';
import { useSocialMedia } from '../contexts/SocialMediaContext';
import { COLORS } from '../constants/colors'; // adjust the path as needed
import {
  MAX_CONTENT_WIDTH,
  BORDER_RADIUS,
  SPACING_TIGHT,
  SPACING_ELEMENT,
  SPACING_COMPACT,
  SPACING_COMPONENT,
  SPACING_BUTTON,
  SPACING_CARD,
  SPACING_CARD_BODY,
  SPACING_GROUP,
  SPACING_SECTION,
  SPACING_HERO,
  SPACING_PAGE,
} from '../constants/layout';
import {
  CODA_EXTRABOLD_DISPLAY_SIZE,
  CODA_EXTRABOLD_MOBILE_SIZE,
  FONT_LARGE_TITLE,
  FONT_LARGE_TITLE_MOBILE,
  FONT_TITLE3,
  FONT_HEADLINE,
  FONT_SUBHEADLINE,
  FONT_BODY,
} from '../constants/typography';
import { formatAddress } from '../utils/address';
import {
  formatBusinessHoursRows,
  getStoreLocalTimeParts,
  to12h,
  windowsForWeekday,
} from '../utils/businessHours';

export default function StoreDetail() {
  const { store, loading } = useContext(StoreContext);
  const { socialMedia } = useSocialMedia();
  const formattedAddress = formatAddress(store?.location);

  const PLATFORMS = [
    { key: 'facebook',  label: 'Facebook',  iconClass: 'fab fa-facebook-f',  baseUrl: 'https://facebook.com/',    color: '#FFFFFF', bg: '#1877F2' },
    { key: 'instagram', label: 'Instagram', iconClass: 'fab fa-instagram',   baseUrl: 'https://instagram.com/',   color: '#FFFFFF', bg: '#E1306C' },
    { key: 'threads',   label: 'Threads',   iconClass: 'fab fa-threads',     baseUrl: 'https://threads.net/@',    color: '#FFFFFF', bg: '#000000' },
    { key: 'tiktok',    label: 'TikTok',    iconClass: 'fab fa-tiktok',      baseUrl: 'https://tiktok.com/@',     color: '#FFFFFF', bg: '#000000' },
    { key: 'youtube',   label: 'YouTube',   iconClass: 'fab fa-youtube',     baseUrl: 'https://youtube.com/@',    color: '#FFFFFF', bg: '#FF0000' },
    { key: 'twitter',   label: 'X',         iconClass: 'fab fa-x-twitter',   baseUrl: 'https://x.com/',           color: '#FFFFFF', bg: '#000000' },
    { key: 'linkedin',  label: 'LinkedIn',  iconClass: 'fab fa-linkedin-in', baseUrl: 'https://linkedin.com/in/', color: '#FFFFFF', bg: '#0A66C2' },
    { key: 'pinterest', label: 'Pinterest', iconClass: 'fab fa-pinterest-p', baseUrl: 'https://pinterest.com/',   color: '#FFFFFF', bg: '#E60023' },
  ];

  const socialLinks = socialMedia
    ? PLATFORMS
        .filter(p => socialMedia[p.key]?.trim())
        .map(p => {
          const value = socialMedia[p.key].trim();
          return {
            href: value.startsWith('http') ? value : `${p.baseUrl}${value}`,
            label: p.label,
            iconClass: p.iconClass,
            color: p.color,
            bg: p.bg,
          };
        })
    : [];

  const findNextOpen = (businessHours) => {
    const { timeZone, daily } = businessHours;
    const { weekday, minutesSinceMidnight } = getStoreLocalTimeParts(timeZone);

    const todayWindows = windowsForWeekday(daily, weekday).map((w) => {
      const [h, m] = w.opensAt.split(':').map(Number);
      return { ...w, openMins: h * 60 + m };
    });
    todayWindows.sort((a, b) => a.openMins - b.openMins);
    for (let w of todayWindows) {
      if (w.openMins > minutesSinceMidnight) {
        return { dayOffset: 0, opensAt: w.opensAt };
      }
    }

    for (let offset = 1; offset <= 7; offset++) {
      const nextDay = ((weekday + offset - 1) % 7) + 1;
      const nextWindows = windowsForWeekday(daily, nextDay);
      if (nextWindows.length > 0) {
        const earliest = nextWindows
          .map((w) => {
            const [h, m] = w.opensAt.split(':').map(Number);
            return { opensAt: w.opensAt, mins: h * 60 + m };
          })
          .sort((a, b) => a.mins - b.mins)[0];
        return { dayOffset: offset, opensAt: earliest.opensAt };
      }
    }

    return null;
  };

  const findCurrentClose = (businessHours) => {
    const { timeZone, daily } = businessHours;
    const { weekday, minutesSinceMidnight } = getStoreLocalTimeParts(timeZone);

    const todayWindows = windowsForWeekday(daily, weekday).map((w) => {
      const [hO, mO] = w.opensAt.split(':').map(Number);
      const [hC, mC] = w.closesAt.split(':').map(Number);
      return {
        opensAt: w.opensAt,
        closesAt: w.closesAt,
        openMins: hO * 60 + mO,
        closeMins: hC * 60 + mC,
      };
    });

    for (let w of todayWindows) {
      if (w.closeMins > w.openMins) {
        if (minutesSinceMidnight >= w.openMins && minutesSinceMidnight < w.closeMins) {
          return { dayOffset: 0, closesAt: w.closesAt };
        }
      } else {
        if (minutesSinceMidnight >= w.openMins || minutesSinceMidnight < w.closeMins) {
          const offset = minutesSinceMidnight >= w.openMins ? 0 : 1;
          return { dayOffset: offset, closesAt: w.closesAt };
        }
      }
    }
    return null;
  };

  const computeOpenStatus = () => {
    if (!store?.businessHours || !Array.isArray(store.businessHours.daily)) {
      return { isOpen: false, nextChange: null };
    }
    const bh = store.businessHours;
    const { timeZone } = bh;
    const { minutesSinceMidnight } = getStoreLocalTimeParts(timeZone);

    const currClose = findCurrentClose(bh);
    if (currClose) {
      let [cH, cM] = currClose.closesAt.split(':').map(Number);
      let closeMins = cH * 60 + cM;
      if (currClose.dayOffset === 1) {
        closeMins += 24 * 60;
      }
      const nowMinsAdjusted =
        currClose.dayOffset === 1 && minutesSinceMidnight < closeMins % (24 * 60)
          ? minutesSinceMidnight + 24 * 60
          : minutesSinceMidnight;
      const minutesUntilClose = closeMins - nowMinsAdjusted;
      return {
        isOpen: true,
        nextChange: {
          type: 'closing',
          timeString: to12h(currClose.closesAt),
          minutesUntil: minutesUntilClose,
        },
      };
    }

    const nextOpen = findNextOpen(bh);
    if (!nextOpen) {
      return { isOpen: false, nextChange: null };
    }
    const { dayOffset, opensAt } = nextOpen;
    let [oH, oM] = opensAt.split(':').map(Number);
    let openMins = oH * 60 + oM + dayOffset * 24 * 60;
    const minutesUntilOpen = openMins - minutesSinceMidnight;
    return {
      isOpen: false,
      nextChange: {
        type: 'opening',
        timeString: to12h(opensAt),
        minutesUntil: minutesUntilOpen,
      },
    };
  };

  const hasBusinessHours =
    !!store?.businessHours &&
    Array.isArray(store.businessHours.daily) &&
    store.businessHours.daily.some(({ opensAt, closesAt }) => opensAt !== closesAt);

  const { isOpen, nextChange } = hasBusinessHours
    ? computeOpenStatus()
    : { isOpen: false, nextChange: null };

  let statusText = '';
  if (hasBusinessHours) {
    statusText = 'Closed';
    if (isOpen && nextChange) {
      statusText = `Open until ${nextChange.timeString}`;
    } else if (!isOpen && nextChange) {
      statusText = `Opens at ${nextChange.timeString}`;
    }
  }

  const renderHoursGrid = () => {
    const rows = formatBusinessHoursRows(store.businessHours);
    if (rows.length === 0) {
      return null;
    }

    return rows.map(({ weekdayValue, isToday, dayLabel, dateLabel, timeText }) => (
      <React.Fragment key={weekdayValue}>
        <div className={`day-cell ${isToday ? 'today' : ''}`}>
          {isToday ? 'Today' : dayLabel}
        </div>
        <div className={`date-cell ${isToday ? 'today' : ''}`}>{dateLabel}</div>
        <div className={`time-cell ${isToday ? 'today' : ''}`}>{timeText}</div>
      </React.Fragment>
    ));
  };

  const mapsUrl = () => {
    if (!formattedAddress) return '#';
    const addressString = encodeURIComponent(formattedAddress);
    return `https://www.google.com/maps/search/?api=1&query=${addressString}`;
  };

  const embedUrl = () => {
    if (!formattedAddress) return '';
    const addressString = encodeURIComponent(formattedAddress);
    return `https://www.google.com/maps?q=${addressString}&output=embed`;
  };

  return (
    <div className={`store-detail-container ${loading ? 'loading' : ''}`}>
      {loading && (
        <div className="inline-loading">
          <div className="spinner" />
        </div>
      )}

      {!loading && !store && <p className="not-found">Store not found.</p>}

      {!loading && store && (
        <div className="hstack-layout">
          {/* ―― LEFT: STORE NAME + SOCIALS ―― */}
          <div className="left-side">
            <h1 className="store-name">{store.name}</h1>
            {hasBusinessHours && (
              <div className={`badge ${isOpen ? 'open' : 'closed'}`}>{statusText}</div>
            )}
            {socialLinks.length > 0 && (
              <div className="social-links">
                {socialLinks.map(link => (
                  <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link"
                    aria-label={link.label}
                    style={{ backgroundColor: link.bg, color: link.color }}
                  >
                    <i className={link.iconClass} aria-hidden="true" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* ―― RIGHT: CONTACT, MAP, HOURS ―― */}
          <div className="right-side">
            {/* ―― CONTACT + MAP SIDE BY SIDE ―― */}
            <div className="contact-map-wrapper">
              {(store.storeContact || store.location) && (
                <div className="card contact-card">
                  <h2 className="section-heading">
                    {store.storeContact?.name
                      ? `Contact ${store.storeContact.name}`
                      : 'Contact'}
                  </h2>

                  {store.storeContact?.phoneNumber && (
                    <p className="contact-info">
                      <span className="material-icons icon">phone</span>
                      <a
                        href={`tel:${(
                          store.storeContact.phoneNumber || ''
                        ).replace(/\D+/g, '')}`}
                        className="link"
                      >
                        {store.storeContact.phoneNumber}
                      </a>
                    </p>
                  )}

                  {store.storeContact?.email && (
                    <p className="contact-info">
                      <span className="material-icons icon">email</span>
                      <a href={`mailto:${store.storeContact.email}`} className="link">
                        {store.storeContact.email}
                      </a>
                    </p>
                  )}

                  {formattedAddress && (
                    <p className="contact-info">
                      <span className="material-icons icon">place</span>
                      <a
                        href={mapsUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link"
                      >
                        {formattedAddress}
                      </a>
                    </p>
                  )}
                </div>
              )}

              {store.location && (
                <div className="card map-card">
                  <iframe
                    title="store-map"
                    src={embedUrl()}
                    width="100%"
                    height="100%"
                    style={{ border: 0, borderRadius: BORDER_RADIUS }}
                    allowFullScreen=""
                    loading="lazy"
                  />
                </div>
              )}
            </div>

            {store.about?.trim() && (
              <div className="card about-card">
                <h2 className="section-heading">About</h2>
                <p className="about-text">{store.about}</p>
              </div>
            )}

            {store.pickUpInstructions?.trim() && (
              <div className="card pickup-card">
                <h2 className="section-heading">Pickup Instructions</h2>
                <p className="pickup-text">{store.pickUpInstructions}</p>
              </div>
            )}

            {store.businessHours && Array.isArray(store.businessHours.daily) && (
              <div className="card hours-card">
                <h2 className="section-heading">Hours</h2>
                <div className="hours-grid">{renderHoursGrid()}</div>
              </div>
            )}
          </div>
        </div>
      )}

      <style jsx>{`
        .store-detail-container {
          max-width: ${MAX_CONTENT_WIDTH};
          margin: 0 auto;
          padding: ${SPACING_BUTTON} ${SPACING_BUTTON} ${SPACING_PAGE};
          min-height: calc(100vh - 100px);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .store-detail-container.loading {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100%;
        }
        .inline-loading {
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid var(--color-border);
          border-top-color: var(--color-text-primary);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .not-found {
          text-align: center;
          color: var(--color-text-secondary);
          margin-top: ${SPACING_GROUP};
          font-size: ${FONT_HEADLINE};
        }

        .hstack-layout {
          display: flex;
          gap: ${SPACING_SECTION};
          align-items: center;
          width: 100%;
        }

        .left-side {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: ${SPACING_COMPONENT};
          text-align: center;
        }

        .right-side {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: ${SPACING_BUTTON};
        }

        .social-links {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: ${SPACING_COMPONENT};
          flex-wrap: wrap;
        }

        .social-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 48px;
          border-radius: 50%;
          border: none;
          font-size: 22px;
          text-decoration: none;
          transition: opacity 0.2s, transform 0.2s;
        }

        .social-link:hover,
        .social-link:focus {
          opacity: 0.8;
          transform: scale(1.08);
        }

        .badge {
          font-size: ${FONT_BODY};
          font-weight: 600;
          padding: ${SPACING_TIGHT} ${SPACING_BUTTON};
          border-radius: 999px;
          text-transform: uppercase;
          display: inline-block;
        }
        .badge.open {
          background-color: var(--color-success-bg);
          color: var(--color-success-text);
        }
        .badge.closed {
          background-color: var(--color-danger-bg);
          color: var(--color-danger-text);
        }

        .store-name {
          margin: 0;
          font-size: ${FONT_LARGE_TITLE};
          font-weight: 600;
          font-family: var(--font-family-base);
          text-transform: uppercase;
          color: var(--color-text-primary);
        }

        .contact-map-wrapper {
          display: flex;
          gap: ${SPACING_BUTTON};
          width: 100%;
        }

        /* Removed box-shadow here */
        .card {
          background-color: var(--color-card-background);
          border: 1px solid var(--color-border);
          border-radius: ${BORDER_RADIUS};
        }

        .contact-card {
          flex: 1;
          padding: ${SPACING_CARD_BODY} ${SPACING_GROUP};
        }
        .contact-info {
          margin: ${SPACING_ELEMENT} 0;
          font-size: ${FONT_BODY};
          display: flex;
          align-items: center;
          gap: ${SPACING_ELEMENT};
          color: var(--color-text-primary);
        }
      .contact-info .icon {
    font-size: ${FONT_TITLE3};
    color: ${COLORS.primary};
  }
       .contact-info .link {
    color: ${COLORS.primary};
    text-decoration: none;
  }
        .contact-info .link:hover {
          text-decoration: underline;
        }

        .map-card {
          flex: 1;
          overflow: hidden;
          height: 200px;
        }

        .about-card {
          padding: ${SPACING_CARD_BODY} ${SPACING_GROUP};
        }
        .about-text {
          margin: 0;
          font-size: ${FONT_BODY};
          color: var(--color-text-primary);
          white-space: pre-wrap;
        }
        .pickup-card {
          padding: ${SPACING_CARD_BODY} ${SPACING_GROUP};
        }
        .pickup-text {
          margin: 0;
          font-size: ${FONT_BODY};
          color: var(--color-text-primary);
          white-space: pre-wrap;
        }
        .hours-card {
          padding: ${SPACING_CARD_BODY} ${SPACING_GROUP};
        }
        .section-heading {
          margin: 0 0 ${SPACING_BUTTON} 0;
          font-size: ${FONT_TITLE3};
          font-family: var(--font-family-base);
          font-weight: 600;
          text-transform: uppercase;
          color: var(--color-text-primary);
          border-bottom: 2px solid var(--color-border);
          padding-bottom: ${SPACING_COMPONENT};
        }
        .hours-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 2fr;
          column-gap: ${SPACING_BUTTON};
          row-gap: ${SPACING_ELEMENT};
          font-size: ${FONT_SUBHEADLINE};
        }
        .day-cell {
          font-weight: 600;
          color: var(--color-text-primary);
        }
        .date-cell {
          color: var(--color-text-secondary);
        }
        .time-cell {
          color: var(--color-text-secondary);
        }
        .closed-day {
          color: var(--color-badge-muted);
        }
        .today {
          font-weight: 600;
          color: var(--color-text-primary);
        }

        @media (max-width: 480px) {
          .store-detail-container {
            align-items: flex-start;
            justify-content: flex-start;
            padding-top: ${SPACING_SECTION};
          }
          .hstack-layout {
            flex-direction: column;
            gap: ${SPACING_GROUP};
            align-items: center;
          }
          .left-side {
            width: 100%;
            flex: none;
          }
          .right-side {
            width: 100%;
            flex: none;
          }
          .store-name {
            font-size: ${FONT_LARGE_TITLE_MOBILE};
          }
          .contact-map-wrapper {
            flex-direction: column;
          }
          .map-card {
            height: 200px;
          }
          .about-card {
            padding: ${SPACING_BUTTON} ${SPACING_CARD};
          }
          .pickup-card {
            padding: ${SPACING_BUTTON} ${SPACING_CARD};
          }
          .hours-card {
            padding: ${SPACING_BUTTON} ${SPACING_CARD};
          }
          .contact-card {
            padding: ${SPACING_BUTTON} ${SPACING_CARD};
          }
          .hours-grid {
            grid-template-columns: 1fr 1fr 1fr;
          }
        }
      `}</style>
    </div>
  );
}
